import { useState } from "react";
import { adminFetch } from "../../lib/adminApi";
import { toast } from "sonner";

export interface HobbyItem {
  id: string;
  title_en: string;
  title_tr: string;
  subtitle_en: string;
  subtitle_tr: string;
  category: "music" | "books" | "films";
  link?: string;
  sort_order: number;
}

export function useHobbiesDraft(
  editingHobby: Partial<HobbyItem> | null,
  setEditingHobby: React.Dispatch<React.SetStateAction<Partial<HobbyItem> | null>>,
  hobbies: HobbyItem[],
  activeDraft: any,
  setActiveDraft: React.Dispatch<React.SetStateAction<any>>
) {
  const handleSaveDraft = async () => {
    if (!editingHobby) return;
    try {
      await adminFetch("/api/admin/drafts", {
        operation: "save",
        item_type: "hobby",
        item_id: editingHobby.id,
        draft_data: editingHobby,
      });
      setActiveDraft(editingHobby);
      toast.success("Hobby draft saved to staging successfully!");
    } catch (err: any) {
      toast.error(err.message || "Failed to save draft");
    }
  };

  const handleDiscardDraft = async () => {
    if (!editingHobby) return;
    if (!confirm("Are you sure you want to discard this staged draft?")) return;
    try {
      await adminFetch("/api/admin/drafts", {
        operation: "delete",
        item_type: "hobby",
        item_id: editingHobby.id,
      });
      setActiveDraft(null);
      const live = hobbies.find((h) => h.id === editingHobby.id);
      setEditingHobby(live || null);
      toast.success("Staged draft discarded successfully!");
    } catch (err: any) {
      toast.error(err.message || "Failed to discard draft");
    }
  };

  return { handleSaveDraft, handleDiscardDraft };
}

async function swapHobbyOrder(hobbyA: HobbyItem, hobbyB: HobbyItem, onRefresh: () => void) {
  try {
    await adminFetch("/api/admin/hobbies", { ...hobbyA, sort_order: hobbyB.sort_order });
    await adminFetch("/api/admin/hobbies", { ...hobbyB, sort_order: hobbyA.sort_order });
    onRefresh();
  } catch (err: any) {
    toast.error(err.message || "Failed to reorder items");
  }
}

const createDefaultHobby = (hobbiesLength: number): Partial<HobbyItem> => ({
  id: `hobby-${Date.now()}`,
  title_en: "",
  title_tr: "",
  subtitle_en: "",
  subtitle_tr: "",
  category: "music",
  link: "",
  sort_order: hobbiesLength,
});

export function useHobbiesManager(hobbies: HobbyItem[], onRefresh: () => void) {
  const [editingHobby, setEditingHobby] = useState<Partial<HobbyItem> | null>(null);
  const [activeDraft, setActiveDraft] = useState<any>(null);

  const { handleSaveDraft, handleDiscardDraft } = useHobbiesDraft(
    editingHobby,
    setEditingHobby,
    hobbies,
    activeDraft,
    setActiveDraft
  );

  const startAdd = () => {
    setActiveDraft(null);
    setEditingHobby(createDefaultHobby(hobbies.length));
  };

  const handleEdit = (hobby: HobbyItem) => {
    setEditingHobby(hobby);
    adminFetch(`/api/admin/drafts?item_type=hobby&item_id=${hobby.id}`)
      .then((res) => {
        if (res?.success && res.draft) {
          setActiveDraft(res.draft);
          setEditingHobby(res.draft);
          toast.info("Unpublished staged draft loaded for editing.");
        } else {
          setActiveDraft(null);
        }
      })
      .catch(() => setActiveDraft(null));
  };

  const handleSave = async () => {
    if (!editingHobby?.title_en || !editingHobby?.subtitle_en) {
      toast.error("Title and Subtitle (English) are required!");
      return;
    }
    try {
      await adminFetch("/api/admin/drafts", {
        operation: "promote",
        item_type: "hobby",
        item_id: editingHobby.id,
        draft_data: editingHobby,
      });
      toast.success("Hobby item saved successfully!");
      setEditingHobby(null);
      setActiveDraft(null);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to save hobby item");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this hobby item?")) return;
    try {
      await adminFetch("/api/admin/hobbies", { operation: "delete", id });
      await adminFetch("/api/admin/drafts", {
        operation: "delete",
        item_type: "hobby",
        item_id: id,
      });
      toast.success("Hobby item deleted successfully!");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete hobby item");
    }
  };

  const handleMove = async (index: number, direction: "up" | "down") => {
    const sortedHobbies = [...hobbies].sort((a, b) => a.sort_order - b.sort_order);
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= sortedHobbies.length) return;

    const hobbyA = sortedHobbies[index];
    const hobbyB = sortedHobbies[swapIndex];
    if (hobbyA && hobbyB) {
      await swapHobbyOrder(hobbyA, hobbyB, onRefresh);
    }
  };

  return {
    editingHobby,
    setEditingHobby,
    activeDraft,
    setActiveDraft,
    startAdd,
    handleEdit,
    handleSaveDraft,
    handleDiscardDraft,
    handleSave,
    handleDelete,
    handleMove
  };
}

export default useHobbiesManager;
